import React, { useState, useEffect } from 'react';
import {
  CalendarDaysIcon,
  ClockIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  UserIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { ukSchedulingAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const EmployeeAvailability = () => {
  const [availabilityData, setAvailabilityData] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedWeek, setSelectedWeek] = useState(getWeekStart(new Date()));
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: '',
    availability_type: 'available',
    start_date: '',
    end_date: '',
    start_time: '09:00',
    end_time: '17:00',
    recurring: false,
    days_of_week: [],
    reason: '',
    notes: ''
  });

  const { user, isAdmin, isManager, isEmployee } = useAuth();

  useEffect(() => {
    fetchEmployees();
    if (isEmployee()) {
      setSelectedEmployee(user.id);
      fetchAvailability(user.id);
    }
  }, []);

  useEffect(() => {
    if (selectedEmployee) {
      fetchAvailability(selectedEmployee);
    }
  }, [selectedEmployee, selectedDate, selectedWeek]);

  function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    const monday = new Date(d.setDate(diff));
    return monday.toISOString().split('T')[0];
  }

  function getWeekEnd(weekStart) {
    const start = new Date(weekStart);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return end.toISOString().split('T')[0];
  }

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await ukSchedulingAPI.getEmployees();
      setEmployees(response.data.employees || []);
      
      // Auto-select first employee for managers/admins
      if ((isManager() || isAdmin()) && response.data.employees?.length > 0) {
        setSelectedEmployee(response.data.employees[0].id);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async (employeeId) => {
    try {
      setLoading(true);
      const params = {
        employee_id: employeeId,
        start_date: viewMode === 'week' ? selectedWeek : selectedDate,
        end_date: viewMode === 'week' ? getWeekEnd(selectedWeek) : selectedDate,
        view_mode: viewMode
      };
      
      const response = await ukSchedulingAPI.getEmployeeAvailability(params);
      setAvailabilityData(response.data.availability || []);
    } catch (error) {
      console.error('Error fetching availability:', error);
      setError('Failed to load availability data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAvailability = async () => {
    try {
      setLoading(true);
      const availabilityData = {
        ...formData,
        employee_id: selectedEmployee,
        company_id: user.companyId,
        created_by: user.id
      };

      await ukSchedulingAPI.createAvailability(availabilityData);
      await fetchAvailability(selectedEmployee);
      setShowAvailabilityModal(false);
      resetForm();
    } catch (error) {
      console.error('Error creating availability:', error);
      setError('Failed to create availability');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAvailability = async (availabilityId) => {
    if (!window.confirm('Are you sure you want to delete this availability entry?')) return;

    try {
      setLoading(true);
      await ukSchedulingAPI.deleteAvailability(availabilityId);
      await fetchAvailability(selectedEmployee);
    } catch (error) {
      console.error('Error deleting availability:', error);
      setError('Failed to delete availability');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      availability_type: 'available',
      start_date: '',
      end_date: '',
      start_time: '09:00',
      end_time: '17:00',
      recurring: false,
      days_of_week: [],
      reason: '',
      notes: ''
    });
  };

  const handleDayToggle = (day) => {
    const currentDays = formData.days_of_week || [];
    const isSelected = currentDays.includes(day);
    
    if (isSelected) {
      setFormData({
        ...formData,
        days_of_week: currentDays.filter(d => d !== day)
      });
    } else {
      setFormData({
        ...formData,
        days_of_week: [...currentDays, day]
      });
    }
  };

  const getAvailabilityForDate = (date) => {
    return availabilityData.filter(av => {
      const avDate = av.date || av.start_date;
      return avDate === date;
    });
  };

  const getAvailabilityStatus = (date) => {
    const dayAvailability = getAvailabilityForDate(date);
    if (dayAvailability.length === 0) return 'unknown';
    
    const hasUnavailable = dayAvailability.some(av => av.availability_type === 'unavailable');
    const hasLeave = dayAvailability.some(av => av.availability_type === 'leave');
    const hasAvailable = dayAvailability.some(av => av.availability_type === 'available');
    
    if (hasLeave) return 'leave';
    if (hasUnavailable && !hasAvailable) return 'unavailable';
    if (hasAvailable && !hasUnavailable) return 'available';
    return 'partial';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800 border-green-200';
      case 'unavailable': return 'bg-red-100 text-red-800 border-red-200';
      case 'leave': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'partial': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available': return <CheckIcon className="h-4 w-4" />;
      case 'unavailable': return <XMarkIcon className="h-4 w-4" />;
      case 'leave': return <CalendarDaysIcon className="h-4 w-4" />;
      case 'partial': return <ExclamationTriangleIcon className="h-4 w-4" />;
      default: return <ClockIcon className="h-4 w-4" />;
    }
  };

  const generateWeekDays = () => {
    const start = new Date(selectedWeek);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      days.push({
        date: date.toISOString().split('T')[0],
        dayName: date.toLocaleDateString('en-GB', { weekday: 'short' }),
        dayNumber: date.getDate(),
        isToday: date.toISOString().split('T')[0] === new Date().toISOString().split('T')[0]
      });
    }
    return days;
  };

  const daysOfWeek = [
    { key: 'monday', label: 'Mon' },
    { key: 'tuesday', label: 'Tue' },
    { key: 'wednesday', label: 'Wed' },
    { key: 'thursday', label: 'Thu' },
    { key: 'friday', label: 'Fri' },
    { key: 'saturday', label: 'Sat' },
    { key: 'sunday', label: 'Sun' }
  ];

  const availabilityTypes = [
    { value: 'available', label: 'Available', color: 'text-green-600' },
    { value: 'unavailable', label: 'Unavailable', color: 'text-red-600' },
    { value: 'leave', label: 'On Leave', color: 'text-yellow-600' }
  ];

  const renderAvailabilityModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Set Availability</h3>
          <button
            onClick={() => setShowAvailabilityModal(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); handleCreateAvailability(); }}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Availability Type *
                </label>
                <select
                  required
                  value={formData.availability_type}
                  onChange={(e) => setFormData({ ...formData, availability_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {availabilityTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Range
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    required
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    type="date"
                    required
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>

            {formData.availability_type === 'available' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Available From
                  </label>
                  <input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Available Until
                  </label>
                  <input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                id="recurring"
                checked={formData.recurring}
                onChange={(e) => setFormData({ ...formData, recurring: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="recurring" className="ml-2 block text-sm text-gray-900">
                Recurring availability
              </label>
            </div>

            {formData.recurring && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recurring Days
                </label>
                <div className="flex flex-wrap gap-2">
                  {daysOfWeek.map(day => (
                    <button
                      key={day.key}
                      type="button"
                      onClick={() => handleDayToggle(day.key)}
                      className={`px-3 py-2 rounded-md text-sm font-medium ${
                        (formData.days_of_week || []).includes(day.key)
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(formData.availability_type === 'unavailable' || formData.availability_type === 'leave') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason
                </label>
                <input
                  type="text"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., Personal appointment, Holiday"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Additional notes..."
              />
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t">
            <button
              type="button"
              onClick={() => setShowAvailabilityModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Availability'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  if (loading && availabilityData.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employee Availability</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage employee availability and time preferences
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setViewMode('week')}
              className={`px-4 py-2 text-sm font-medium rounded-l-md border ${
                viewMode === 'week'
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setViewMode('month')}
              className={`px-4 py-2 text-sm font-medium rounded-r-md border-t border-r border-b ${
                viewMode === 'month'
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Month
            </button>
          </div>
          <button
            onClick={() => setShowAvailabilityModal(true)}
            className="btn-primary inline-flex items-center"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Set Availability
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Employee Selection (for managers/admins) */}
      {(isAdmin() || isManager()) && employees.length > 0 && (
        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex items-center space-x-4">
            <UserIcon className="h-5 w-5 text-gray-400" />
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Employee
              </label>
              <select
                value={selectedEmployee || ''}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Choose employee...</option>
                {employees.map(employee => (
                  <option key={employee.id} value={employee.id}>
                    {employee.first_name} {employee.last_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {selectedEmployee && (
        <>
          {/* Navigation Controls */}
          <div className="bg-white shadow rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => {
                    if (viewMode === 'week') {
                      const prev = new Date(selectedWeek);
                      prev.setDate(prev.getDate() - 7);
                      setSelectedWeek(prev.toISOString().split('T')[0]);
                    } else {
                      const prev = new Date(selectedDate);
                      prev.setMonth(prev.getMonth() - 1);
                      setSelectedDate(prev.toISOString().split('T')[0]);
                    }
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <div className="text-lg font-medium text-gray-900">
                  {viewMode === 'week' ? (
                    `Week of ${new Date(selectedWeek).toLocaleDateString('en-GB', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}`
                  ) : (
                    new Date(selectedDate).toLocaleDateString('en-GB', { 
                      month: 'long', 
                      year: 'numeric' 
                    })
                  )}
                </div>
                
                <button
                  onClick={() => {
                    if (viewMode === 'week') {
                      const next = new Date(selectedWeek);
                      next.setDate(next.getDate() + 7);
                      setSelectedWeek(next.toISOString().split('T')[0]);
                    } else {
                      const next = new Date(selectedDate);
                      next.setMonth(next.getMonth() + 1);
                      setSelectedDate(next.toISOString().split('T')[0]);
                    }
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              
              <button
                onClick={() => {
                  if (viewMode === 'week') {
                    setSelectedWeek(getWeekStart(new Date()));
                  } else {
                    setSelectedDate(new Date().toISOString().split('T')[0]);
                  }
                }}
                className="text-sm text-primary-600 hover:text-primary-800 font-medium"
              >
                Today
              </button>
            </div>
          </div>

          {/* Week View */}
          {viewMode === 'week' && (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="grid grid-cols-7 gap-px bg-gray-200">
                {generateWeekDays().map((day) => {
                  const status = getAvailabilityStatus(day.date);
                  const dayAvailability = getAvailabilityForDate(day.date);
                  
                  return (
                    <div key={day.date} className="bg-white min-h-[120px] p-2">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className={`text-sm font-medium ${
                            day.isToday ? 'text-primary-600' : 'text-gray-900'
                          }`}>
                            {day.dayName}
                          </div>
                          <div className={`text-sm ${
                            day.isToday ? 'text-primary-600 font-semibold' : 'text-gray-500'
                          }`}>
                            {day.dayNumber}
                          </div>
                        </div>
                        <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getStatusColor(status)}`}>
                          {getStatusIcon(status)}
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        {dayAvailability.map((av, index) => (
                          <div
                            key={index}
                            className={`text-xs p-1 rounded border ${getStatusColor(av.availability_type)}`}
                          >
                            <div className="font-medium capitalize">
                              {av.availability_type}
                            </div>
                            {av.start_time && av.end_time && (
                              <div className="text-xs opacity-75">
                                {av.start_time} - {av.end_time}
                              </div>
                            )}
                            {av.reason && (
                              <div className="text-xs opacity-75 truncate">
                                {av.reason}
                              </div>
                            )}
                            {(isAdmin() || isManager() || av.created_by === user.id) && (
                              <button
                                onClick={() => handleDeleteAvailability(av.id)}
                                className="text-red-600 hover:text-red-800 text-xs float-right mt-1"
                              >
                                <XMarkIcon className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="bg-white shadow rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Legend</h3>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-green-100 border border-green-200 rounded mr-2"></div>
                <span className="text-sm text-gray-700">Available</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-red-100 border border-red-200 rounded mr-2"></div>
                <span className="text-sm text-gray-700">Unavailable</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-yellow-100 border border-yellow-200 rounded mr-2"></div>
                <span className="text-sm text-gray-700">On Leave</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-blue-100 border border-blue-200 rounded mr-2"></div>
                <span className="text-sm text-gray-700">Partial</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gray-100 border border-gray-200 rounded mr-2"></div>
                <span className="text-sm text-gray-700">Unknown</span>
              </div>
            </div>
          </div>
        </>
      )}

      {showAvailabilityModal && renderAvailabilityModal()}
    </div>
  );
};

export default EmployeeAvailability;